import React, { useState, useEffect, useRef } from 'react';
import { useEvangelioFilters, EvangelioFilterPills } from './components/EvangelioFilters';
import SearchModal from './components/SearchModal';
import { useFavorites } from './hooks/useFavorites';

// El santoral guarda una ficha por santo y por año (San Nicolás de Tolentino
// existe en 2024, 2025 y 2026, con textos distintos). Para el listado se muestra
// una sola por santo, la del año más reciente; las anteriores siguen en la base
// de datos y se alcanzan por búsqueda.
const dedupSantos = (santos) => {
  const porSanto = new Map();

  santos.forEach(santo => {
    if (!santo.date) return;
    const fecha = new Date(santo.date);
    const titulo = (santo.title || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
    const clave = `${fecha.getMonth()}-${fecha.getDate()}-${titulo}`;

    const previo = porSanto.get(clave);
    if (!previo || fecha > new Date(previo.date)) {
      porSanto.set(clave, santo);
    }
  });

  // Los santos sin fecha no se pueden agrupar, así que pasan tal cual.
  return [...santos.filter(santo => !santo.date), ...porSanto.values()];
};

const ArticleListView = ({ sectionTitle, articles, onBack, onSelectArticle, initialFilter = null }) => {
  // Filtro por defecto basado en la sección. `initialFilter` permite que quien
  // navega hasta aquí preseleccione uno (el pill de Santos agustinos abre el
  // mes en curso); si ese filtro no existe en la sección, se descarta más abajo.
  const defaultFilter = sectionTitle === 'Santos Agustinos' ? 'todos' : 'recent';
  const [activeFilter, setActiveFilter] = useState(initialFilter || defaultFilter);

  // La vista se reutiliza entre secciones, así que hay que reaccionar al cambio
  // y no solo al montaje.
  useEffect(() => {
    setActiveFilter(initialFilter || (sectionTitle === 'Santos Agustinos' ? 'todos' : 'recent'));
  }, [sectionTitle, initialFilter]);
  
  // Estado para el modal de búsqueda
  const [showSearchModal, setShowSearchModal] = useState(false);
  
  // Hook de favoritos
  const { getFavoriteArticles, favoritesCount } = useFavorites();
  
  // Hook especializado para filtros de Evangelio (siempre se llama, pero solo se usa si es Evangelio)
  const evangelioFilters = useEvangelioFilters(
    sectionTitle === 'Evangelio' ? articles : [], 
    defaultFilter
  );
  
  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha no disponible';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Función para obtener imagen con fallbacks
  const getImageUrl = (article) => {
    if (article.image || article.imageUrl || article.thumbnail) {
      return article.image || article.imageUrl || article.thumbnail;
    }
    
    // Para artículos sin imagen, retornar null para usar el icono CSS
    return null;
  };

  // Obtener referencia bíblica del subtítulo o descripción
  const getBiblicalReference = (article) => {
    // Buscar patrones como "Mt 11,25-27" o "Mateo 12, 14-21"
    const text = article.subtitle || article.description || '';
    const match = text.match(/([A-Za-z]+\s*\d+,\s*\d+(?:-\d+)?)/);
    return match ? match[1] : '';
  };

  // Obtener quote/descripción sin la referencia bíblica
  const getQuote = (article) => {
    const desc = article.description || article.subtitle || '';
    // Remover la referencia bíblica si existe
    const ref = getBiblicalReference(article);
    if (ref) {
      return desc.replace(ref, '').replace(/^[,\s-]+|[,\s-]+$/g, '').trim();
    }
    return desc;
  };

  // Lista base de la sección. En Santos Agustinos se depura de repetidos por año.
  const sectionArticles = sectionTitle === 'Santos Agustinos'
    ? dedupSantos(articles || [])
    : articles;

  // Filtros disponibles basados en la sección
  const getFilters = () => {
    const commonFilters = [
      { id: 'recent', label: '✨ Todos', emoji: '✨' },
      { id: 'thisWeek', label: '📅 Esta semana', emoji: '📅' }
    ];
    
    // Agregar filtro de favoritos si hay artículos favoritos
    if (favoritesCount > 0) {
      commonFilters.push({
        id: 'favorites',
        label: `⭐ Favoritos (${favoritesCount})`,
        emoji: '⭐'
      });
    }

    if (sectionTitle === 'Santos Agustinos') {
      // Crear filtros por mes basados en los santos disponibles
      const santosFilters = [{ id: 'todos', label: '📿 Todos', emoji: '📿' }];
      
      // Agrupar santos por mes
      const santosPorMes = {};
      const mesesNombres = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ];
      const mesesEmojis = ['❄️', '🌸', '🌱', '🌷', '🌺', '☀️', '🌞', '🌻', '🍂', '🍁', '🎄', '✨'];
      
      if (sectionArticles && sectionArticles.length > 0) {
        sectionArticles.forEach(santo => {
          if (santo.date) {
            const fecha = new Date(santo.date);
            const mes = fecha.getMonth(); // 0-11
            if (!santosPorMes[mes]) {
              santosPorMes[mes] = 0;
            }
            santosPorMes[mes]++;
          }
        });
        
        // Crear filtros para los meses que tienen santos
        Object.keys(santosPorMes).forEach(mesIndex => {
          const mes = parseInt(mesIndex);
          const count = santosPorMes[mes];
          santosFilters.push({
            id: `mes-${mes}`,
            label: `${mesesEmojis[mes]} ${mesesNombres[mes]} (${count})`,
            emoji: mesesEmojis[mes],
            mes: mes
          });
        });
      }
      
      return santosFilters;
    }

    if (sectionTitle === 'Evangelio') {
      return [
        ...commonFilters,
        { id: 'ordinary', label: '📖 Tiempo Ordinario', emoji: '📖' },
        { id: 'advent', label: '🎄 Adviento', emoji: '🎄' },
        { id: 'lent', label: '✨ Cuaresma', emoji: '✨' },
        { id: 'easter', label: '🌅 Pascua', emoji: '🌅' }
      ];
    }

    return [
      ...commonFilters,
      { id: 'popular', label: '⭐ Populares', emoji: '⭐' },
      { id: 'featured', label: '🔥 Destacados', emoji: '🔥' }
    ];
  };

  // Función para filtrar artículos
  const getFilteredArticles = () => {
    if (!sectionArticles || sectionArticles.length === 0) return [];
    
    // Filtro de favoritos (funciona para todas las secciones)
    if (effectiveFilter === 'favorites') {
      return getFavoriteArticles(sectionArticles).sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateB - dateA;
      });
    }
    
    // Usar filtros especializados para Evangelio
    if (sectionTitle === 'Evangelio') {
      return evangelioFilters.filteredArticles;
    }
    
    if (sectionTitle === 'Santos Agustinos') {
      if (effectiveFilter === 'todos') {
        return sectionArticles; // Mostrar todos los santos
      }
      
      // Filtrar por mes, en orden de celebración dentro del mes
      if (effectiveFilter.startsWith('mes-')) {
        const mesIndex = parseInt(effectiveFilter.replace('mes-', ''));
        return sectionArticles
          .filter(santo => {
            if (santo.date) {
              const fecha = new Date(santo.date);
              return fecha.getMonth() === mesIndex;
            }
            return false;
          })
          .sort((a, b) => new Date(a.date).getDate() - new Date(b.date).getDate());
      }
    }
    
    // Para otras secciones, aplicar filtros básicos
    switch (effectiveFilter) {
      case 'recent':
        // Mostrar todos los artículos ordenados por fecha (más recientes primero)
        return articles.sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });
      
      case 'thisWeek':
        // Artículos de esta semana
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        
        return articles.filter(article => {
          if (article.date) {
            const articleDate = new Date(article.date);
            return articleDate >= oneWeekAgo;
          }
          return false;
        }).sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });
      
      case 'popular':
        // Ordenar por popularidad (artículos con audio primero, luego por fecha)
        return articles.sort((a, b) => {
          if (a.hasAudio && !b.hasAudio) return -1;
          if (!a.hasAudio && b.hasAudio) return 1;
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });
      
      case 'featured':
        // Artículos destacados (con audio y más recientes)
        return articles.filter(article => article.hasAudio || article.featured)
          .sort((a, b) => {
            const dateA = new Date(a.date || 0);
            const dateB = new Date(b.date || 0);
            return dateB - dateA;
          });
      
      default:
        return articles;
    }
  };

  const filters = getFilters();

  // El filtro preseleccionado puede no existir todavía: en diciembre no hay
  // santos, y al abrir desde el pill el santoral completo puede estar aún en
  // vuelo. Se resuelve en cada render en vez de reescribir el estado, para que
  // el mes se recupere solo en cuanto llegan los datos.
  const effectiveFilter = (sectionTitle === 'Santos Agustinos' && !filters.some(f => f.id === activeFilter))
    ? 'todos'
    : activeFilter;

  // La barra de filtros hace scroll horizontal y el mes que llega preseleccionado
  // puede quedar fuera de pantalla. Se centra sobre su propio contenedor (no con
  // scrollIntoView, que arrastraría también la página).
  const filtersRef = useRef(null);
  const activeFilterRef = useRef(null);

  useEffect(() => {
    const boton = activeFilterRef.current;
    const barra = filtersRef.current;
    if (!boton || !barra) return;

    const destino = boton.offsetLeft - (barra.clientWidth - boton.clientWidth) / 2;
    const maximo = barra.scrollWidth - barra.clientWidth;
    barra.scrollTo({
      left: Math.max(0, Math.min(destino, maximo)),
      behavior: 'smooth'
    });
  }, [effectiveFilter, filters.length]);

  const filteredArticles = getFilteredArticles();

  console.log('📋 ArticleListView recibió:', {
    sectionTitle,
    articlesCount: articles?.length,
    filteredCount: filteredArticles?.length,
    activeFilter: effectiveFilter,
    firstArticle: articles?.[0]
  });

  return (
    <div className="app-container">
      <div className="content-container">
        {/* Encabezado personalizado con gradiente y botones */}
        <div className="relative">
          {/* Fondo rojo con gradiente y elementos del encabezado */}
          <div
            className="text-white px-5 pb-14 rounded-b-3xl"
            style={{
              background: 'linear-gradient(to bottom, #c82318 0%, #c82318 15%, #a01916 100%)',
              paddingTop: 'max(env(safe-area-inset-top) + 20px, 40px)'
            }}
          >
            {/* Barra superior con botón de regreso y búsqueda */}
            <div className="flex items-center justify-between mb-4">
              {/* Botón de regreso */}
              <button
                onClick={() => {
                  if (onBack) {
                    onBack();
                  } else {
                    console.warn('⚠️ onBack no está definido');
                  }
                }}
                className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white"
              >
                ←
              </button>
              {/* Botón de búsqueda */}
              <button
                onClick={() => {
                  console.log('🔍 Abriendo búsqueda para:', sectionTitle);
                  setShowSearchModal(true);
                }}
                className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white"
              >
                🔍
              </button>
            </div>
            {/* Título y subtítulo dentro del encabezado */}
            <h1 className="text-3xl font-bold mb-2 text-center">{sectionTitle}</h1>
            <p className="text-sm text-center opacity-90 leading-relaxed">
              {sectionTitle === 'Evangelio'
                ? '¡La Palabra de Dios contigo! Lee o escucha el Evangelio del día.'
                : `Explora todo el contenido de ${sectionTitle} disponible para ti.`}
            </p>
          </div>
          
          {/* Contenedor blanco superpuesto para las pills de filtro */}
          {sectionTitle === 'Evangelio' ? (
            <EvangelioFilterPills 
              filters={evangelioFilters.filters}
              activeFilter={evangelioFilters.activeFilter}
              onFilterChange={evangelioFilters.setActiveFilter}
            />
          ) : (
            <div className="px-5 absolute inset-x-0" style={{ top: '100%', transform: 'translateY(-32px)' }}>
              <div
                ref={filtersRef}
                className="bg-white rounded-3xl shadow-md flex gap-3 overflow-x-auto custom-scrollbar px-4 py-3"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                {filters.map((filter) => (
                  <button
                    key={filter.id}
                    ref={effectiveFilter === filter.id ? activeFilterRef : null}
                    onClick={() => setActiveFilter(filter.id)}
                    className={`px-4 py-3 rounded-2xl font-semibold whitespace-nowrap text-sm transition-all duration-300 ${
                      effectiveFilter === filter.id
                        ? 'text-white transform -translate-y-1'
                        : 'bg-white text-gray-600 hover:border-red-500 transform hover:-translate-y-0.5'
                    }`}
                    style={
                      effectiveFilter === filter.id
                        ? {
                            background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)',
                            boxShadow: '0 6px 20px rgba(230, 44, 25, 0.3)'
                          }
                        : {
                            background: '#f8f9fa',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                            border: '2px solid transparent'
                          }
                    }
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3 mt-20">
          {filteredArticles && filteredArticles.length > 0 ? (
            filteredArticles.map((article, index) => (
              <div
                key={article.id || index}
                onClick={() => {
                  console.log('📎 Click en artículo:', article.title);
                  if (onSelectArticle) {
                    onSelectArticle(article);
                  } else {
                    console.error('❌ onSelectArticle no está definido');
                  }
                }}
                className="modern-card mb-5 cursor-pointer relative overflow-hidden"
                style={{
                  animation: `slideInUp 0.6s ease forwards`,
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="flex items-center gap-4 p-5">
                {/* Imagen del artículo o icono */}
                {getImageUrl(article) ? (
                  <img 
                    src={getImageUrl(article)}
                    alt={article.title}
                    className="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
                    onError={(e) => {
                      // Si falla la imagen, mostrar icono CSS
                      const iconDiv = document.createElement('div');
                      iconDiv.className = 'w-20 h-20 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0';
                      iconDiv.style.background = 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)';
                      iconDiv.style.color = 'white';
                      iconDiv.textContent = '📖';
                      e.target.parentNode.replaceChild(iconDiv, e.target);
                    }}
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0" 
                       style={{ background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)', color: 'white' }}>
                    📖
                  </div>
                )}

                  {/* Contenido del artículo */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 leading-tight">
                      {article.title}
                    </h3>
                    
                    {getBiblicalReference(article) && (
                      <p className="text-sm text-gray-500 font-semibold mb-1">
                        {getBiblicalReference(article)}
                      </p>
                    )}
                    
                    {getQuote(article) && (
                      <p 
                        className="text-sm text-gray-600 italic leading-relaxed relative whitespace-pre-wrap"
                        style={{
                          paddingLeft: '12px'
                        }}
                      >
                        <span 
                          className="absolute left-0 top-0 text-red-500 text-lg font-bold"
                          style={{ marginTop: '-2px' }}
                        >
                          "
                        </span>
                        {getQuote(article)}
                        <span className="text-red-500 text-lg font-bold">"</span>
                      </p>
                    )}

                    {/* Metadatos en la parte inferior */}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-gray-400">
                        {formatDate(article.date)}
                      </span>
                      
                      <div className="flex items-center gap-2">
                        {article.hasAudio && (
                          <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                            🎵 Audio
                          </span>
                        )}
                        {article.author && article.author !== 'Agustinos' && (
                          <span className="text-xs text-gray-500">
                            {article.author}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            /* Empty State */
            <div className="text-center py-16">
              <div className="text-6xl mb-5 opacity-50">
                {sectionTitle === 'Santos Agustinos' ? '📿' : '📖'}
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-3">
                {sectionTitle === 'Santos Agustinos' 
                  ? `No hay santos para ${effectiveFilter === 'todos' ? 'mostrar' : 'este mes'}`
                  : `No hay ${sectionTitle.toLowerCase()} disponibles`
                }
              </h3>
              <p className="text-base text-gray-500 leading-relaxed">
                {sectionTitle === 'Santos Agustinos'
                  ? 'Prueba seleccionando otro mes en los filtros'
                  : 'Revisa tu conexión a internet o intenta más tarde'
                }
              </p>
            </div>
          )}

          {/* Loading State - placeholder para futuro uso */}
          <div className="loading-state text-center py-10" style={{ display: 'none' }}>
            <div 
              className="w-10 h-10 border-3 border-gray-200 border-t-red-500 rounded-full animate-spin mx-auto mb-4"
              style={{
                borderWidth: '3px',
                borderTopColor: '#e62c19'
              }}
            />
            <p className="text-gray-500">Cargando más {sectionTitle.toLowerCase()}...</p>
          </div>
        </div>

        {/* Floating Action Button */}
        <button 
          className="fixed right-5 bottom-8 w-14 h-14 rounded-full text-white shadow-lg transition-all duration-300 hover:scale-110 z-50"
          style={{
            background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)',
            boxShadow: '0 8px 25px rgba(230, 44, 25, 0.4)'
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          title={`${sectionTitle} de hoy`}
        >
          <span className="text-2xl">📖</span>
        </button>

        {/* Espaciado inferior para el FAB */}
        <div style={{ height: '80px' }}></div>
      </div>

      {/* Modal de búsqueda */}
      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        currentSection={sectionTitle}
        onArticleClick={(article, section) => {
          console.log('📎 Artículo seleccionado desde búsqueda:', article.title);
          onSelectArticle(article);
        }}
      />
    </div>
  );
};

export default ArticleListView;